import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAgentStats, useAgents } from '../api/hooks';

const autonomyStyles: Record<string, string> = {
  Intern: 'bg-slate-100 text-slate-600 border-slate-200',
  Junior: 'bg-blue-50 text-blue-700 border-blue-100',
  Senior: 'bg-emerald-50 text-emerald-700 border-emerald-100',
  Principal: 'bg-purple-50 text-purple-700 border-purple-100',
};

const statusStyles: Record<string, { dot: string; text: string; key: string }> = {
  active: { dot: 'bg-emerald-500 animate-pulse', text: 'text-emerald-700', key: 'active' },
  pending: { dot: 'bg-slate-400', text: 'text-slate-600', key: 'pending' },
  suspended: { dot: 'bg-amber-500', text: 'text-amber-700', key: 'suspended' },
  decommissioned: { dot: 'bg-red-400', text: 'text-red-700', key: 'decommissioned' },
};

function TrustBar({ score }: { score: number }) {
  const color = score >= 80 ? 'bg-emerald-500' : score >= 60 ? 'bg-amber-500' : 'bg-red-500';
  return (
    <div className="flex items-center gap-2">
      <span className="text-sm font-black text-text-main">{score || '--'}</span>
      <div className="h-1.5 w-16 rounded-full bg-slate-100">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${score}%` }}></div>
      </div>
    </div>
  );
}

export default function AIAgents() {
  const navigate = useNavigate();
  const { t } = useTranslation(['agents', 'common']);
  const { data: agentStats, loading: statsLoading, error: statsError } = useAgentStats();
  const { data: agents, loading: agentsLoading, error: agentsError } = useAgents();

  const loading = statsLoading || agentsLoading;
  const error = statsError || agentsError;

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-text-main">{t('list.title')}</h1>
          <p className="mt-1 text-sm text-text-muted">{t('list.subtitle')}</p>
        </div>
        <button className="btn-primary" onClick={() => navigate('/ai-agents/deploy')}>
          <span className="material-symbols-outlined" style={{ fontSize: 18 }}>add_circle</span>
          {t('list.deploy')}
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : error ? (
        <div className="text-center py-20 text-red-500">{t('common:errors.loadAgentsFailed')}</div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {(Array.isArray(agentStats) ? agentStats : []).map((stat) => (
              <div key={stat.label} className="stat-card group">
                <span className="text-[10px] font-bold uppercase tracking-wider text-text-muted">{stat.label}</span>
                <div className="mt-2">
                  <span className="text-3xl font-bold text-text-main">{stat.value}</span>
                  <p className="mt-1 text-xs text-text-faint">{stat.sub}</p>
                </div>
                <span className="material-symbols-outlined absolute -right-2 -top-2 text-slate-900 opacity-5 transition-opacity group-hover:opacity-10" style={{ fontSize: 80, fontVariationSettings: "'FILL' 1" }}>
                  {stat.icon}
                </span>
              </div>
            ))}
          </div>

          {/* overflow-x-auto, not overflow-hidden: the table is wider than the
              page on smaller screens, and hidden overflow cut off the Status
              column with no way to scroll to it. */}
          <div className="overflow-x-auto rounded-xl border border-border-subtle bg-white shadow-sm">
            <table className="w-full min-w-[900px]">
              <thead>
                <tr className="border-b border-border-subtle bg-slate-50/50">
                  <th className="px-6 py-3 text-left text-[11px] font-extrabold uppercase tracking-widest text-text-faint">{t('list.table.name')}</th>
                  <th className="px-6 py-3 text-left text-[11px] font-extrabold uppercase tracking-widest text-text-faint">{t('list.table.did')}</th>
                  <th className="px-6 py-3 text-left text-[11px] font-extrabold uppercase tracking-widest text-text-faint">{t('list.table.autonomy')}</th>
                  <th className="px-6 py-3 text-left text-[11px] font-extrabold uppercase tracking-widest text-text-faint">{t('list.table.trust')}</th>
                  <th className="px-6 py-3 text-left text-[11px] font-extrabold uppercase tracking-widest text-text-faint">{t('list.table.status')}</th>
                  <th className="w-12 px-3 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {(Array.isArray(agents) ? agents : []).map((agent) => {
                  const st = statusStyles[agent.status] ?? statusStyles.pending;
                  return (
                    <tr key={agent.did} onClick={() => navigate(`/ai-agents/${agent.did}`)} className="cursor-pointer border-b border-border-subtle transition-colors hover:bg-slate-50/60">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="flex size-10 items-center justify-center rounded-lg bg-blue-50">
                            <span className="material-symbols-outlined text-primary" style={{ fontSize: 20 }}>smart_toy</span>
                          </div>
                          <div>
                            <p className="text-sm font-bold tracking-tight text-text-main">{agent.name}</p>
                            <p className="text-[10px] font-bold uppercase tracking-wide text-text-faint">{agent.category}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="inline-block rounded border border-slate-200 bg-slate-100 px-1.5 py-0.5 font-mono text-xs font-semibold text-text-main">
                          {agent.did}
                        </span>
                        <div className="mt-1">
                          <span className="text-[11px] font-bold text-primary">{t('common:labels.key', { key: agent.key })}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-block rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide ${autonomyStyles[agent.autonomy] ?? ''}`}>
                          {agent.autonomy}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <TrustBar score={agent.trust} />
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide ${st.text}`}>
                          <span className={`size-1.5 rounded-full ${st.dot}`}></span>
                          {t(`statusLabels.${st.key}`)}
                        </span>
                      </td>
                      <td className="px-3 py-4">
                        <span className="material-symbols-outlined text-text-faint" style={{ fontSize: 16 }}>chevron_right</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
